import { SectionTitle } from 'pages/Shipments/Dashboard/SectionTitle';
import * as React from 'react';
import { OutstandingFeesBreakdown } from 'services/clients/insights';
import { Row } from 'pages/Shipments/components/HorizontalGraph';
import pluralize from 'pluralize';
import { ChargesBreakdown } from './ChargesBreakdown';
import { useTrackedHistoricalParams } from '../params';
import { useBreakdownData } from './hooks';
import { formatDollars } from './OverTimeSection/ChargesOverTime/utility';

const TOOLTIP_COPY =
  'How much per diem accrued during the date range selected, broken down by either Port, SSL, Container #, or Ocean BOL.';

export const BreakdownSection: React.FC = () => {
  const { searchParams } = useTrackedHistoricalParams();
  const { breakdown } = searchParams;

  const { data, isLoading, showHistoric, errors } = useBreakdownData(breakdown);

  const rows = React.useMemo(() => {
    // Sort the combined records by the current amount and then generate the rows for the graph
    return data.map((c) => {
      const row: Row = {
        key: c.key,
        label: c.label,
        data: [
          ...(c.current
            ? [
                {
                  detail:
                    breakdown === OutstandingFeesBreakdown.Container
                      ? `${formatDollars(c.current.amount)}`
                      : `${c.current.numberOfContainers} ${pluralize(
                          'container',
                          c.current.numberOfContainers
                        )} · ${formatDollars(c.current.amount)}`,
                  blueAmount: c.current.disputableAmount,
                  redAmount: c.current.amount - c.current.disputableAmount,
                  variant: 'current' as const,
                },
              ]
            : []),
          ...(c.historic
            ? [
                {
                  detail:
                    breakdown === OutstandingFeesBreakdown.Container
                      ? `${formatDollars(c.historic.amount)}`
                      : `${c.historic.numberOfContainers} ${pluralize(
                          'container',
                          c.historic.numberOfContainers
                        )} · ${formatDollars(c.historic.amount)}`,
                  blueAmount: c.historic.disputableAmount,
                  redAmount: c.historic.amount - c.historic.disputableAmount,
                  variant: 'historic' as const,
                },
              ]
            : []),
        ],
      };
      return row;
    });
  }, [data, breakdown]);

  return (
    <div className="space-y-4 px-4 flex-grow flex flex-col min-h-0">
      <SectionTitle tooltip={TOOLTIP_COPY} title="Per diem charges breakdown" />
      <ChargesBreakdown
        data={rows}
        showHistoric={showHistoric}
        isLoading={isLoading}
        errors={errors}
      />
    </div>
  );
};
BreakdownSection.displayName = 'Shipments.HistoricalPerDiem.BreakdownSection';
